import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom'
import { getClassDetails, getClassStudents, getSubjectList, updateSclass } from "../../../redux/sclassRelated/sclassHandle";
import { deleteUser } from '../../../redux/userRelated/userHandle';
import { useHomeroomTeacherTerm } from "../../../hooks/useHomeroomTeacherTerm";
import { CLASS_TEACHER_REQUIRES_SUBJECT_MSG } from "../../../utils/classTeacherUtils";
import {
    removeTeachSubject,
    updateTeachSubject,
} from "../../../redux/teacherRelated/teacherHandle";
import HomeroomAssignSubjectsFields from "../../../components/admin/HomeroomAssignSubjectsFields";
import {
    getTeacherSubjectIdsInClass,
    HOMEROOM_ASSIGN_SUBJECTS_MSG,
    syncTeacherSubjectsForClass,
} from "../../../utils/homeroomAssignSubjects";
import { getSecondaryFormLevel } from "../../../utils/classScopeMatch";
import Form3SubmitToStudentsPanel from "../../../components/class/Form3SubmitToStudentsPanel";
import { getApiBaseUrl } from "../../../config/api";
import { dedupeSubjectsById, getSubjectTeacherLabel } from "../../../utils/dedupeSubjects";
import {
    Box,
    Button,
    Alert,
    FormControl,
    FormHelperText,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Tab,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import { resetSubjects } from "../../../redux/sclassRelated/sclassSlice";
import { BlueButton, GreenButton, PurpleButton } from "../../../components/buttonStyles";
import TableTemplate from "../../../components/TableTemplate";
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import SpeedDialTemplate from "../../../components/SpeedDialTemplate";
import Popup from "../../../components/Popup";
import ConfirmDeleteDialog from "../../../components/ConfirmDeleteDialog";
import DeleteIcon from "@mui/icons-material/Delete";
import PostAddIcon from '@mui/icons-material/PostAdd';

const ClassDetails = () => {
    const params = useParams()
    const navigate = useNavigate()
    const dispatch = useDispatch();
    const { subjectsList, sclassStudents, sclassDetails, loading, subloading, error, getresponse } = useSelector((state) => state.sclass);
    const { currentUser } = useSelector((state) => state.user);
    const homeroomTerm = useHomeroomTeacherTerm();

    const classID = params.id

    const [editTeacherId, setEditTeacherId] = useState("");
    const [formTeacherSubjectIds, setFormTeacherSubjectIds] = useState([]);
    const [editCapacity, setEditCapacity] = useState("");
    const [editRoom, setEditRoom] = useState("");
    const [teachersPick, setTeachersPick] = useState([]);
    const [savingSettings, setSavingSettings] = useState(false);
    const [subjectSelectionOpen, setSubjectSelectionOpen] = useState(false);

    const secondaryFormLevel = getSecondaryFormLevel(sclassDetails);
    const isSubjectChoiceClass =
        secondaryFormLevel === "Form 3" || secondaryFormLevel === "Form 4";
    const subjectCount = Array.isArray(subjectsList) ? dedupeSubjectsById(subjectsList).length : 0;

    useEffect(() => {
        dispatch(getClassDetails(classID, "Sclass"));
        dispatch(getSubjectList(classID, "ClassSubjects"))
        dispatch(getClassStudents(classID));
    }, [dispatch, classID])

    useEffect(() => {
        if (!sclassDetails?.id || sclassDetails.message) return;
        const tid = sclassDetails.classTeacher?._id ?? sclassDetails.classTeacher?.id ?? "";
        setEditTeacherId(tid || "");
        setEditCapacity(sclassDetails.studentCapacity != null ? String(sclassDetails.studentCapacity) : "");
        setEditRoom(sclassDetails.room || "");
        setSubjectSelectionOpen(Boolean(sclassDetails.subjectSelectionOpen));
        if (tid) {
            const teacher =
                teachersPick.find((t) => (t._id || t.id) === tid) ||
                (Array.isArray(sclassDetails?.teachers)
                    ? sclassDetails.teachers.find((t) => (t._id || t.id) === tid)
                    : null);
            setFormTeacherSubjectIds(getTeacherSubjectIdsInClass(teacher, classID));
        } else {
            setFormTeacherSubjectIds([]);
        }
    }, [sclassDetails, teachersPick, classID]);

    useEffect(() => {
        if (!currentUser?._id) return;
        let cancelled = false;
        (async () => {
            try {
                const { data } = await axios.get(`${getApiBaseUrl()}/Teachers/${currentUser._id}`);
                if (!cancelled && Array.isArray(data)) setTeachersPick(data);
            } catch {
                /* ignore */
            }
        })();
        return () => { cancelled = true; };
    }, [currentUser?._id]);

    if (error) {
        console.log(error)
    }

    const [value, setValue] = useState('1');

    const handleChange = (event, newValue) => {
        setValue(newValue);
    };

    const [showPopup, setShowPopup] = useState(false);
    const [message, setMessage] = useState("");
    const [deleteDialog, setDeleteDialog] = useState(null);

    const className = sclassDetails?.sclassName || "this class";

    const refreshClassData = () => {
        dispatch(getClassStudents(classID));
        dispatch(resetSubjects());
        dispatch(getSubjectList(classID, "ClassSubjects"));
    };

    const closeDeleteDialog = () => setDeleteDialog(null);

    const openDeleteDialog = ({ title, description, confirmWord, deleteID, address }) => {
        setDeleteDialog({
            title,
            description,
            confirmWord,
            onConfirm: async () => {
                const outcome = await dispatch(deleteUser(deleteID, address));
                if (outcome?.success) {
                    refreshClassData();
                    return;
                }
                setMessage("Could not complete the delete. Try again or check the server logs.");
                setShowPopup(true);
            },
        });
    };

    const deleteHandler = (deleteID, address, itemName) => {
        if (address === "Student") {
            openDeleteDialog({
                title: `Remove student: ${itemName}`,
                description:
                    "This will permanently delete this student and their fee records, attendance, and marks.",
                confirmWord: itemName,
                deleteID,
                address,
            });
            return;
        }
        if (address === "Subject") {
            openDeleteDialog({
                title: `Remove subject: ${itemName}`,
                description:
                    "This will permanently remove this subject from the class and related attendance and marks.",
                confirmWord: itemName,
                deleteID,
                address,
            });
            return;
        }
        if (address === "StudentsClass") {
            openDeleteDialog({
                title: `Delete all students in ${className}`,
                description: "This will permanently delete every student in this class. This cannot be undone.",
                confirmWord: "DELETE ALL STUDENTS",
                deleteID,
                address,
            });
            return;
        }
        if (address === "SubjectsClass") {
            openDeleteDialog({
                title: `Delete all subjects in ${className}`,
                description: "This will permanently delete every subject for this class. This cannot be undone.",
                confirmWord: "DELETE ALL SUBJECTS",
                deleteID,
                address,
            });
        }
    };

    const handleConfirmDelete = async () => {
        if (deleteDialog?.onConfirm) {
            await deleteDialog.onConfirm();
        }
        closeDeleteDialog();
    };

    const subjectColumns = [
        { id: 'name', label: 'Subject Name', minWidth: 180 },
        { id: 'teacher', label: 'Teacher', minWidth: 140 },
    ]

    const dedupedSubjects = useMemo(
        () => (Array.isArray(subjectsList) ? dedupeSubjectsById(subjectsList) : []),
        [subjectsList],
    );

    const subjectRows = dedupedSubjects.length > 0 && dedupedSubjects.map((subject) => {
        return {
            name: subject.subName,
            teacher: getSubjectTeacherLabel(subject),
            id: subject._id || subject.id,
        };
    })

    const SubjectsButtonHaver = ({ row }) => {
        return (
            <>
                <IconButton onClick={() => deleteHandler(row.id, "Subject", row.name)}>
                    <DeleteIcon color="error" />
                </IconButton>
                <BlueButton
                    variant="contained"
                    onClick={() => {
                        navigate(`/Admin/class/subject/${classID}/${row.id}`)
                    }}
                >
                    View
                </BlueButton >
            </>
        );
    };

    const subjectActions = [
        {
            icon: <PostAddIcon color="primary" />, name: 'Add New Subject',
            action: () => navigate("/Admin/addsubject/" + classID)
        },
        {
            icon: <DeleteIcon color="error" />, name: 'Delete All Subjects',
            action: () => deleteHandler(classID, "SubjectsClass")
        }
    ];

    const ClassSubjectsSection = () => {
        const hasSubjects = dedupedSubjects.length > 0;

        if (!hasSubjects) {
            return (
                <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, textAlign: "center" }}>
                    <Typography variant="h6" fontWeight={700} gutterBottom>
                        No subjects in this class yet
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Add subjects for this class, then assign teachers to each subject.
                    </Typography>
                    <GreenButton variant="contained" onClick={() => navigate("/Admin/addsubject/" + classID)}>
                        Add subjects
                    </GreenButton>
                </Paper>
            );
        }

        return (
            <>
                {isSubjectChoiceClass ? (
                    <Box sx={{ mb: 2 }}>
                        <Form3SubmitToStudentsPanel
                            formLevel={secondaryFormLevel}
                            classId={classID}
                            subjectCount={dedupedSubjects.length}
                            selectionOpen={subjectSelectionOpen}
                            onUpdated={(open) => {
                                setSubjectSelectionOpen(open);
                                dispatch(getClassDetails(classID, "Sclass"));
                            }}
                        />
                    </Box>
                ) : null}
                <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
                    <Typography variant="h5" fontWeight={700}>
                        Subjects ({dedupedSubjects.length})
                    </Typography>
                    <GreenButton variant="contained" onClick={() => navigate("/Admin/addsubject/" + classID)}>
                        Add more subjects
                    </GreenButton>
                </Stack>
                <TableTemplate buttonHaver={SubjectsButtonHaver} columns={subjectColumns} rows={subjectRows} />
                <SpeedDialTemplate actions={subjectActions} />
            </>
        );
    };

    const studentColumns = [
        { id: 'name', label: 'Name', minWidth: 170 },
        { id: 'rollNum', label: 'Roll Number', minWidth: 100 },
    ]

    const studentRows = sclassStudents.map((student) => {
        return {
            name: student.name,
            rollNum: student.rollNum,
            id: student._id,
        };
    })

    const StudentsButtonHaver = ({ row }) => {
        return (
            <>
                <IconButton onClick={() => deleteHandler(row.id, "Student", row.name)}>
                    <PersonRemoveIcon color="error" />
                </IconButton>
                <BlueButton
                    variant="contained"
                    onClick={() => navigate("/Admin/students/student/" + row.id)}
                >
                    View
                </BlueButton>
                <PurpleButton
                    variant="contained"
                    onClick={() =>
                        navigate("/Admin/students/student/attendance/" + row.id)
                    }
                >
                    Attendance
                </PurpleButton>
            </>
        );
    };

    const studentActions = [
        {
            icon: <PersonAddAlt1Icon color="primary" />, name: 'Add New Student',
            action: () => navigate("/Admin/class/addstudents/" + classID)
        },
        {
            icon: <PersonAddAlt1Icon color="secondary" />, name: 'Bulk upload CSV',
            action: () => navigate(`/Admin/class/${classID}/bulk-upload`)
        },
        {
            icon: <PersonRemoveIcon color="error" />, name: 'Delete All Students',
            action: () => deleteHandler(classID, "StudentsClass")
        },
    ];

    const ClassStudentsSection = () => {
        return (
            <>
                {getresponse ? (
                    <>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
                            <GreenButton
                                variant="contained"
                                onClick={() => navigate("/Admin/class/addstudents/" + classID)}
                            >
                                Add Students
                            </GreenButton>
                        </Box>
                    </>
                ) : (
                    <>
                        <Typography variant="h5" gutterBottom>
                            Students List:
                        </Typography>

                        <TableTemplate buttonHaver={StudentsButtonHaver} columns={studentColumns} rows={studentRows} />
                        <SpeedDialTemplate actions={studentActions} />
                    </>
                )}
            </>
        )
    }

    const homeroomTeacherOptions = useMemo(() => {
        if (!Array.isArray(teachersPick) || teachersPick.length === 0) return [];
        return [...teachersPick]
            .map((t) => ({
                _id: t._id || t.id,
                name: t.name,
            }))
            .sort((a, b) => String(a.name).localeCompare(String(b.name)));
    }, [teachersPick]);

    const classSubjects = useMemo(
        () => dedupedSubjects,
        [dedupedSubjects],
    );

    const handleFormTeacherChange = (teacherId) => {
        setEditTeacherId(teacherId);
        if (!teacherId) {
            setFormTeacherSubjectIds([]);
            return;
        }
        const teacher = teachersPick.find((t) => (t._id || t.id) === teacherId);
        setFormTeacherSubjectIds(getTeacherSubjectIdsInClass(teacher, classID));
    };

    const toggleFormTeacherSubject = (subjectId) => {
        setFormTeacherSubjectIds((prev) => {
            const set = new Set(prev);
            if (set.has(subjectId)) set.delete(subjectId);
            else set.add(subjectId);
            return [...set];
        });
    };

    const saveClassSettings = async () => {
        if (editTeacherId && !formTeacherSubjectIds.length) {
            setMessage(HOMEROOM_ASSIGN_SUBJECTS_MSG);
            setShowPopup(true);
            return;
        }
        setSavingSettings(true);
        if (editTeacherId) {
            const teacher = teachersPick.find((t) => (t._id || t.id) === editTeacherId);
            const syncOutcome = await syncTeacherSubjectsForClass(dispatch, {
                teacherId: editTeacherId,
                classId: classID,
                selectedSubjectIds: formTeacherSubjectIds,
                teacher,
                updateTeachSubject,
                removeTeachSubject,
            });
            if (!syncOutcome.success) {
                setSavingSettings(false);
                setMessage(syncOutcome.message || CLASS_TEACHER_REQUIRES_SUBJECT_MSG);
                setShowPopup(true);
                return;
            }
        }
        const outcome = await dispatch(
            updateSclass(classID, {
                classTeacherId: editTeacherId || "",
                studentCapacity: editCapacity === "" ? null : editCapacity,
                room: editRoom,
            })
        );
        setSavingSettings(false);
        if (outcome?.success === false) {
            setMessage(outcome.message || "Could not save class settings.");
            setShowPopup(true);
            return;
        }
        setMessage("Class settings saved.");
        setShowPopup(true);
        dispatch(getClassDetails(classID, "Sclass"));
        dispatch(getSubjectList(classID, "ClassSubjects"));
    };

    const ClassTeachersSection = () => {
        const rows = Array.isArray(sclassDetails?.teachers) ? sclassDetails.teachers : [];
        if (rows.length === 0) {
            return (
                <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
                    <Typography color="text.secondary">
                        No subject teachers are assigned to this class yet. Add teachers from the teacher flow and assign them to this class.
                    </Typography>
                </Paper>
            );
        }
        return (
            <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Subject focus</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((t) => (
                        <TableRow key={t._id || t.id} hover>
                            <TableCell>{t.name}</TableCell>
                            <TableCell>{t.email}</TableCell>
                            <TableCell>{t.teachSubject || "—"}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
            </Paper>
        );
    };

    const ClassDetailsSection = () => {
        const numberOfSubjects = dedupedSubjects.length;
        const numberOfStudents = sclassStudents.length;
        const cap = sclassDetails?.studentCapacity;
        const enrolled = sclassDetails?._count?.students ?? numberOfStudents;
        const capLabel = cap != null && cap > 0 ? `${enrolled} / ${cap}` : `${enrolled} enrolled`;

        return (
            <Stack spacing={3}>
                <Box>
                    <Typography variant="h5" fontWeight={800} gutterBottom>
                        {sclassDetails?.sclassName ?? "Class"}
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" gap={1}>
                        <Typography variant="body2" color="text.secondary">
                            Subjects: <strong>{numberOfSubjects}</strong>
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            · Students: <strong>{capLabel}</strong>
                        </Typography>
                    </Stack>
                </Box>

                <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
                    <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                        {homeroomTerm.assignOnClass}, capacity & venue
                    </Typography>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <InputLabel id="cd-class-teacher">{homeroomTerm.assignOnClass}</InputLabel>
                                <Select
                                    labelId="cd-class-teacher"
                                    label={homeroomTerm.assignOnClass}
                                    value={editTeacherId}
                                    onChange={(e) => handleFormTeacherChange(e.target.value)}
                                >
                                    <MenuItem value="">
                                        <em>None</em>
                                    </MenuItem>
                                    {homeroomTeacherOptions.map((t) => (
                                        <MenuItem key={t._id} value={t._id}>
                                            {t.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                                <FormHelperText>
                                    Pick a teacher, then choose at least one subject they will teach in this class.
                                </FormHelperText>
                            </FormControl>
                            {editTeacherId ? (
                                <HomeroomAssignSubjectsFields
                                    homeroomLabel={
                                        homeroomTeacherOptions.find((t) => t._id === editTeacherId)?.name
                                    }
                                    classId={classID}
                                    showClassSelect={false}
                                    subjects={classSubjects}
                                    subjectsLoading={subloading}
                                    selectedSubjectIds={formTeacherSubjectIds}
                                    onToggleSubject={toggleFormTeacherSubject}
                                    currentTeacherId={editTeacherId}
                                />
                            ) : null}
                            {editTeacherId && homeroomTeacherOptions.length === 0 ? (
                                <Alert severity="warning" sx={{ mt: 1 }}>
                                    Add teachers under Staff first, then assign them here.
                                </Alert>
                            ) : null}
                        </Grid>
                        {isSubjectChoiceClass ? (
                            <Grid item xs={12}>
                                <Form3SubmitToStudentsPanel
                                    formLevel={secondaryFormLevel}
                                    classId={classID}
                                    subjectCount={subjectCount}
                                    selectionOpen={subjectSelectionOpen}
                                    onUpdated={(open) => {
                                        setSubjectSelectionOpen(open);
                                        dispatch(getClassDetails(classID, "Sclass"));
                                    }}
                                />
                            </Grid>
                        ) : null}
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Student capacity"
                                type="number"
                                value={editCapacity}
                                onChange={(e) => setEditCapacity(e.target.value)}
                                inputProps={{ min: 1, step: 1 }}
                                helperText="Leave empty for no limit."
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Room / venue"
                                value={editRoom}
                                onChange={(e) => setEditRoom(e.target.value)}
                            />
                        </Grid>
                    </Grid>
                    <Stack direction="row" spacing={1.5} sx={{ mt: 2 }} justifyContent="flex-end">
                        <Button variant="contained" onClick={saveClassSettings} disabled={savingSettings} sx={{ borderRadius: 2 }}>
                            {savingSettings ? "Saving…" : "Save settings"}
                        </Button>
                    </Stack>
                </Paper>

                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} flexWrap="wrap">
                    <GreenButton variant="contained" onClick={() => navigate("/Admin/class/addstudents/" + classID)}>
                        Add Students
                    </GreenButton>
                    <Button variant="outlined" onClick={() => navigate(`/Admin/class/${classID}/bulk-upload`)}>
                        Bulk upload CSV
                    </Button>
                    <GreenButton variant="contained" onClick={() => navigate("/Admin/addsubject/" + classID)}>
                        Add Subjects
                    </GreenButton>
                    <Button variant="outlined" onClick={() => navigate(`/Admin/classes/results/${classID}`)}>
                        View Results
                    </Button>
                </Stack>
            </Stack>
        );
    }

    return (
        <>
            {loading ? (
                <div>Loading...</div>
            ) : (
                <>
                    <Box sx={{ width: "100%", typography: "body1" }}>
                        <TabContext value={value}>
                            <Box
                                sx={{
                                    position: "sticky",
                                    top: 0,
                                    zIndex: 2,
                                    bgcolor: "background.paper",
                                    borderBottom: 1,
                                    borderColor: "divider",
                                    mb: 2,
                                    isolation: "isolate",
                                }}
                            >
                                <TabList
                                    onChange={handleChange}
                                    variant="fullWidth"
                                    aria-label="Class sections"
                                    sx={{
                                        minHeight: 48,
                                        overflow: "hidden",
                                        "& .MuiTabs-scroller": {
                                            overflow: "hidden !important",
                                        },
                                        "& .MuiTab-root": {
                                            textTransform: "none",
                                            fontWeight: 600,
                                            minWidth: 0,
                                            maxWidth: "100%",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                        },
                                    }}
                                >
                                    <Tab label="Details" value="1" />
                                    <Tab label="Subjects" value="2" />
                                    <Tab label="Students" value="3" />
                                    <Tab label="Teachers" value="4" />
                                </TabList>
                            </Box>
                            <TabPanel value="1" sx={{ px: 0, pt: 0 }}>
                                <ClassDetailsSection />
                            </TabPanel>
                            <TabPanel value="2" sx={{ px: 0, pt: 0 }}>
                                <ClassSubjectsSection />
                            </TabPanel>
                            <TabPanel value="3" sx={{ px: 0, pt: 0 }}>
                                <ClassStudentsSection />
                            </TabPanel>
                            <TabPanel value="4" sx={{ px: 0, pt: 0 }}>
                                <ClassTeachersSection />
                            </TabPanel>
                        </TabContext>
                    </Box>
                </>
            )}
            <Popup message={message} setShowPopup={setShowPopup} showPopup={showPopup} />
            <ConfirmDeleteDialog
                open={Boolean(deleteDialog)}
                onClose={closeDeleteDialog}
                onConfirm={handleConfirmDelete}
                title={deleteDialog?.title ?? ""}
                description={deleteDialog?.description ?? ""}
                confirmWord={deleteDialog?.confirmWord ?? ""}
            />
        </>
    );
};

export default ClassDetails;